import React from "react";
import {
  Box,
  Button,
  Paper,
  Portal,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import WestRoundedIcon from "@mui/icons-material/WestRounded";
import { activityApi } from "../../../api/activity";

// WP-LANDING-EDITOR-ORIENTATION
// STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING
// PURPOSE: a short orientation before the long editor tutorial. It only points at
// existing controls; it never saves, publishes or changes landing configuration.
// PRIVACY: analytics contains step keys/timing only, never landing copy or form values.

const OVERLAY_Z = 2147482200;
const PADDING = 8;
const BRAND = { orange: "#F35A3A" };

const STEPS = Object.freeze([
  {
    key: "toolbar",
    selector: '[data-wp-editor-coach-target="toolbar"]',
    title: "Ésta es la cabina del editor",
    body: "Arriba vas a encontrar las acciones globales: cambiar vista, previsualizar, guardar y publicar. Editar y publicar son cosas distintas: podés trabajar tranquilo sobre un borrador.",
  },
  {
    key: "device",
    selector: '[data-wp-editor-coach-target="device"]',
    title: "Revisá siempre cómo se ve en cada pantalla",
    body: "Podés mirar computadora, tablet y celular sin cambiar la web pública. Es una vista de trabajo para evitar que algo quede bien sólo en escritorio.",
  },
  {
    key: "canvas",
    selector: '[data-wp-editor-coach-target="canvas"]',
    title: "El centro es tu página real",
    body: "Hacé click sobre una sección, texto, imagen o botón para editar esa pieza. No necesitás configurar toda la web de una vez: trabajá por partes.",
  },
  {
    key: "guide",
    selector: '[data-wp-editor-coach-target="guide"]',
    title: "WebProfit te propone qué mejorar después",
    body: "La guía inferior mira el avance del borrador y te lleva a la próxima tarea útil. Podés seguirla o ignorarla y editar libremente.",
    optionalTarget: true,
  },
  {
    key: "save",
    selector: '[data-wp-editor-coach-target="save"]',
    title: "Guardar no publica",
    body: "Guardar conserva el borrador. Tus clientes no ven ese cambio todavía. Usalo seguido para trabajar sin miedo.",
  },
  {
    key: "publish",
    selector: '[data-wp-editor-coach-target="publish"]',
    title: "Publicar es el momento de hacerlo visible",
    body: "Cuando estés conforme, Publicar revisa el borrador y recién entonces actualiza la web pública. Podés volver a este recorrido cuando quieras.",
  },
]);

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function visibleElement(selector) {
  if (!selector || typeof document === "undefined") return null;
  try {
    return Array.from(document.querySelectorAll(selector)).find((node) => {
      if (!(node instanceof HTMLElement)) return false;
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      return rect.width > 2 && rect.height > 2 && style.display !== "none" && style.visibility !== "hidden";
    }) || null;
  } catch {
    return null;
  }
}

function blocker(extra = {}) {
  return {
    position: "fixed",
    zIndex: OVERLAY_Z,
    bgcolor: "rgba(6,8,12,.78)",
    pointerEvents: "auto",
    backdropFilter: "blur(1.5px)",
    ...extra,
  };
}

function Spotlight({ rect }) {
  if (!rect || typeof window === "undefined") return <Box sx={blocker({ inset: 0 })} />;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const left = clamp(rect.left - PADDING, 0, width);
  const top = clamp(rect.top - PADDING, 0, height);
  const right = clamp(rect.right + PADDING, 0, width);
  const bottom = clamp(rect.bottom + PADDING, 0, height);
  return (
    <>
      <Box sx={blocker({ left: 0, top: 0, width: "100vw", height: top })} />
      <Box sx={blocker({ left: 0, top, width: left, height: Math.max(0, bottom - top) })} />
      <Box sx={blocker({ left: right, top, width: Math.max(0, width - right), height: Math.max(0, bottom - top) })} />
      <Box sx={blocker({ left: 0, top: bottom, width: "100vw", height: Math.max(0, height - bottom) })} />
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 1,
          pointerEvents: "none",
          left,
          top,
          width: Math.max(1, right - left),
          height: Math.max(1, bottom - top),
          borderRadius: 2.6,
          border: "2px solid rgba(243,90,58,.96)",
          boxShadow: "0 0 0 5px rgba(243,90,58,.17), 0 0 42px rgba(243,90,58,.34)",
        }}
      />
    </>
  );
}

function track(project, action, metadata = {}) {
  if (!project) return;
  activityApi.trackPanel({
    action,
    projectId: String(project?.id || project?._id || ""),
    projectSlug: String(project?.slug || ""),
    projectName: String(project?.name || ""),
    path: typeof window !== "undefined" ? window.location.pathname : "",
    journey: {
      channel: "panel",
      path: typeof window !== "undefined" ? window.location.pathname : "",
      step: Number(metadata.stepIndex || 0),
    },
    metadata: {
      analyticsOnly: true,
      telemetryVersion: 1,
      ...metadata,
    },
  }).catch(() => null);
}

export default function LandingEditorIntroCoach({
  open = false,
  project,
  source = "editor",
  onClose,
  onComplete,
}) {
  const [stepIndex, setStepIndex] = React.useState(0);
  const [target, setTarget] = React.useState(null);
  const [rect, setRect] = React.useState(null);
  const startedAtRef = React.useRef(Date.now());
  const stepStartedAtRef = React.useRef(Date.now());

  React.useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    startedAtRef.current = Date.now();
    stepStartedAtRef.current = Date.now();
    track(project, "onboarding_editor_orientation_started", { source, stepIndex: 0 });
  }, [open, project, source]);

  React.useEffect(() => {
    if (!open) return undefined;
    const step = STEPS[stepIndex] || STEPS[0];
    let misses = 0;
    const locate = () => {
      const found = visibleElement(step.selector);
      if (!found) {
        misses += 1;
        setTarget(null);
        setRect(null);
        if (step.optionalTarget && misses >= 8) {
          setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
        }
        return;
      }
      misses = 0;
      setTarget(found);
      const nextRect = found.getBoundingClientRect();
      setRect(nextRect);
      if (nextRect.top < 78 || nextRect.bottom > window.innerHeight - 78) {
        found.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };
    locate();
    const timer = window.setInterval(locate, 180);
    const update = () => {
      if (!target?.isConnected) return;
      setRect(target.getBoundingClientRect());
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, stepIndex, target]);

  React.useEffect(() => {
    if (!open) return;
    const step = STEPS[stepIndex] || STEPS[0];
    stepStartedAtRef.current = Date.now();
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex,
      stepKey: step.key,
      orientationResult: "viewed",
    });
  }, [open, project, source, stepIndex]);

  if (!open) return null;

  const step = STEPS[stepIndex] || STEPS[0];
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  const cardWidth = Math.min(440, Math.max(300, viewportWidth - 24));
  const estimatedHeight = 300;
  const canRight = Boolean(rect && rect.right + cardWidth + 22 <= viewportWidth);
  const canLeft = Boolean(rect && rect.left - cardWidth - 22 >= 0);
  const left = rect
    ? canRight
      ? rect.right + 14
      : canLeft
        ? rect.left - cardWidth - 14
        : clamp((viewportWidth - cardWidth) / 2, 12, viewportWidth - cardWidth - 12)
    : Math.max(12, (viewportWidth - cardWidth) / 2);
  const top = rect
    ? (canRight || canLeft)
      ? clamp(rect.top, 12, Math.max(12, viewportHeight - estimatedHeight - 12))
      : rect.bottom + estimatedHeight + 24 <= viewportHeight
        ? rect.bottom + 12
        : clamp(rect.top - estimatedHeight - 12, 12, Math.max(12, viewportHeight - estimatedHeight - 12))
    : Math.max(12, viewportHeight / 2 - estimatedHeight / 2);

  const close = () => {
    track(project, "onboarding_editor_orientation_completed", {
      source,
      stepIndex,
      stepKey: step.key,
      orientationResult: "closed",
      dwellMs: Math.max(0, Date.now() - startedAtRef.current),
    });
    onClose?.();
  };

  const next = () => {
    track(project, "onboarding_editor_orientation_step", {
      source,
      stepIndex,
      stepKey: step.key,
      orientationResult: "completed",
      dwellMs: Math.max(0, Date.now() - stepStartedAtRef.current),
    });
    if (stepIndex >= STEPS.length - 1) {
      track(project, "onboarding_editor_orientation_completed", {
        source,
        stepIndex,
        stepKey: step.key,
        orientationResult: "completed",
        dwellMs: Math.max(0, Date.now() - startedAtRef.current),
      });
      onComplete?.();
      return;
    }
    setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const previous = () => setStepIndex((current) => Math.max(0, current - 1));

  return (
    <Portal>
      <Spotlight rect={target && rect ? rect : null} />
      <Paper
        role="dialog"
        aria-label="Orientación del editor web"
        elevation={24}
        sx={{
          position: "fixed",
          zIndex: OVERLAY_Z + 8,
          top,
          left,
          width: cardWidth,
          maxHeight: "calc(100dvh - 24px)",
          overflowY: "auto",
          p: { xs: 1.7, sm: 2.1 },
          borderRadius: 3.5,
          bgcolor: "rgba(18,20,24,.985)",
          color: "#fff",
          border: "1px solid rgba(243,90,58,.34)",
          boxShadow: "0 32px 100px rgba(0,0,0,.58)",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={.8} alignItems="center">
              <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
              <Typography sx={{ fontSize: 10.8, fontWeight: 1000, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.58)" }}>
                Editor · {stepIndex + 1}/{STEPS.length}
              </Typography>
            </Stack>
            <Button startIcon={<CloseRoundedIcon />} size="small" onClick={close} sx={{ minWidth: 0, color: "rgba(255,255,255,.55)", textTransform: "none", fontWeight: 800 }}>
              Cerrar
            </Button>
          </Stack>

          <Box>
            <Typography sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 1000, lineHeight: 1.12 }}>
              {step.title}
            </Typography>
            <Typography sx={{ mt: .75, fontSize: 13.5, lineHeight: 1.6, color: "rgba(255,255,255,.75)" }}>
              {step.body}
            </Typography>
          </Box>

          {!target ? (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,.46)" }}>
              Esta pieza no está visible con el tamaño o modo actual. Podés continuar sin bloquear el editor.
            </Typography>
          ) : null}

          <Stack direction="row" spacing={.8} useFlexGap flexWrap="wrap">
            <Button
              variant="contained"
              endIcon={<EastRoundedIcon />}
              onClick={next}
              sx={{ bgcolor: BRAND.orange, color: "#17181C", textTransform: "none", fontWeight: 1000, boxShadow: "none", "&:hover": { bgcolor: "#FF714F" } }}
            >
              {stepIndex >= STEPS.length - 1 ? "Empezar a editar" : "Siguiente"}
            </Button>
            {stepIndex > 0 ? (
              <Button startIcon={<WestRoundedIcon />} onClick={previous} sx={{ color: "rgba(255,255,255,.65)", textTransform: "none", fontWeight: 850 }}>
                Anterior
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </Portal>
  );
}
